'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../card';
import { Button } from '../button';
import { Alert, AlertDescription } from '../alert';
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.resetError = () => {
            this.setState({
                hasError: false,
                error: null,
                errorInfo: null,
            });
        };
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }
    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            error,
        };
    }
    componentDidCatch(error, errorInfo) {
        this.setState({
            error,
            errorInfo,
        });
        // Call the onError callback if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }
    }
    render() {
        if (this.state.hasError && this.state.error && this.state.errorInfo) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                const FallbackComponent = this.props.fallback;
                return (_jsx(FallbackComponent, { error: this.state.error, errorInfo: this.state.errorInfo, resetError: this.resetError }));
            }
            // Default error UI
            return (_jsx("div", { className: "min-h-screen flex items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-2xl", children: [_jsx(CardHeader, { children: _jsx(CardTitle, { className: "text-red-600", children: "Something went wrong" }) }), _jsxs(CardContent, { className: "space-y-4", children: [_jsx(Alert, { variant: "destructive", children: _jsx(AlertDescription, { children: "An unexpected error occurred. Please try refreshing the page or contact support if the problem persists." }) }), this.props.showDetails && (_jsxs("details", { className: "mt-4", children: [_jsx("summary", { className: "cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900", children: "Show error details" }), _jsxs("div", { className: "mt-2 p-4 bg-gray-50 rounded border", children: [_jsxs("div", { className: "text-sm", children: [_jsx("strong", { children: "Error:" }), _jsx("pre", { className: "mt-1 whitespace-pre-wrap font-mono text-xs text-red-600", children: this.state.error.toString() })] }), _jsxs("div", { className: "mt-3 text-sm", children: [_jsx("strong", { children: "Stack trace:" }), _jsx("pre", { className: "mt-1 whitespace-pre-wrap font-mono text-xs text-gray-600", children: this.state.errorInfo.componentStack })] })] })] })), _jsxs("div", { className: "flex space-x-2", children: [_jsx(Button, { onClick: this.resetError, children: "Try again" }), _jsx(Button, { variant: "outline", onClick: () => window.location.reload(), children: "Refresh page" })] })] })] }) }));
        }
        return this.props.children;
    }
}
export function withErrorBoundary(Component, errorBoundaryProps) {
    const WrappedComponent = (props) => {
        return (_jsx(ErrorBoundary, { ...errorBoundaryProps, children: _jsx(Component, { ...props }) }));
    };
    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
    return WrappedComponent;
}
